import { call, all, select, type CallEffect } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { DICOMViewerSlice } from '@restore/DICOMViewer/DICOMViewer.slice';
import { clinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { dispatch, getUrlParams } from '@restore/helpers';
import {
  readerTaskSlice,
  selectReaderTaskState,
} from 'src/store-redux/readerTask/readerTask.slice';
import {
  readerSubTaskSlice,
  selectReaderSubTaskState,
} from 'src/store-redux/readerSubTask/readerSubTask.slice';
import type { ReaderTaskState } from 'src/store-redux/readerTask/readerTask.types';
import type { ReaderSubTaskState } from 'src/store-redux/readerSubTask/readerSubTask.types';
import { selectAuthState } from '@restore/auth/auth.slice';
import { readerViewerPageSelector } from 'src/store-redux/readerTaskOrchestration/readerTaskOrchestration.slice';
import {
  mapUserRoleToDICOMResourceEndpoint,
  mapUserRoleToDicomSeriesEndpoint,
  mapUserRoleToTaskEndpoint,
} from 'src/store-redux/readerTaskOrchestration/workers/viewerModeMapper';
import { fetchClinicalTrialInventory } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { fetchClinicalTrialContextWorker } from 'src/store-redux/clinicalTrialContext/clinicalTrialContext.saga';
import { fetchClinicalTrialInventoryWorker } from 'src/store-redux/clinicalTrialInventory/clinicalTrialInventory.saga';
import {
  fetchReaderDICOMResourceWorker,
  fetchReaderTaskWorker,
  fetchReadingDicomStudiesWorker,
} from 'src/store-redux/readerTask/readerTask.saga';
import { fetchReaderSubTaskWorker } from 'src/store-redux/readerSubTask/readerSubTask.saga';

const { fetchNeededDataSuccess, fetchNeededDataFailure } =
  DICOMViewerSlice.actions;

const { fetchClinicalTrialContext } = clinicalTrialContextSlice.actions;

const {
  fetchReaderTask,
  fetchReaderDICOMResource,
  fetchReadingDicomStudies,
  resetReadingDicomStudies,
} = readerTaskSlice.actions;

const { fetchReaderSubTask } = readerSubTaskSlice.actions;

export function* fetchReaderNeededDataWorker(): SagaIterator {
  const {
    id: clinicalTrialId,
    taskId,
    subTaskId: urlSubTaskId,
  } = yield call(getUrlParams);

  if (!(clinicalTrialId && taskId)) {
    return;
  }

  /**
   * Which visit is being scored is the orchestrator's answer, not the URL's
   *
   * Plain terms
   * -----------
   * A reading that spans several visits is opened in two steps: first the app
   * works out which visit the reader should land on and moves the page there,
   * then it opens the viewer. Asking the address bar which visit we are on can
   * therefore come a moment too early — and the answer "none" makes the whole
   * reading look like an ordinary single-visit one, so its earlier visits are
   * never fetched and nothing anywhere reports a problem.
   *
   * Technical
   * ---------
   * `handleGroupTaskSaga` navigates to the sub-task route and returns on its
   * first pass, dispatching `runViewer` only on the second; `window.location`
   * is whatever react-router has committed by then. The orchestration slice is
   * written synchronously by `start`/`load` and is already what
   * `prepareReaderStudy` reads, so taking the sub-task from the same place
   * keeps the fetcher and the payload builder from disagreeing.
   *
   * The slice wins whenever it is talking about THIS reading, so that the two
   * can never name different visits: `prepareReaderStudy` looks the sub-task up
   * by the slice's id alone, and a visit fetched under one id but looked up
   * under another ends in `alert('subTask not found')`. The URL is the fallback
   * for the cases the slice cannot answer — a detached window that carries its
   * own, and the moment after a switch when the slice still names the previous
   * parent's child (C3).
   */
  const orchestration = yield select(readerViewerPageSelector);

  const orchestrationSubTaskId =
    orchestration?.taskId === taskId ? orchestration?.subTaskId : '';

  const subTaskId = orchestrationSubTaskId || urlSubTaskId || undefined;

  const authState = yield select(selectAuthState);
  const selectedRole = authState?.data?.selectedRole;
  const dicomResourceEndpoint =
    mapUserRoleToDICOMResourceEndpoint(selectedRole);

  // The reader task orchestrator already loaded this task before it triggered
  // runViewer → this worker (parent via readerTask slice, sub-task via
  // readerSubTask slice keyed by subTaskId). Re-fetching it here is a duplicate
  // request, so skip the task leg when the store already holds the wanted task.
  // (subTaskId set → the child in readerSubTask.data; otherwise → readerTask.data.)
  const readerTaskState: ReaderTaskState = yield select(selectReaderTaskState);
  const readerSubTaskState: ReaderSubTaskState = yield select(
    selectReaderSubTaskState
  );
  const isSubTaskAlreadyLoaded =
    subTaskId != null && !!readerSubTaskState?.data?.[subTaskId];

  /**
   * The PARENT task is a separate need from the task being scored
   *
   * Plain terms
   * -----------
   * A reading that spans several visits has an owner — the reading itself —
   * and the visit the reader is scoring. The viewer needs both: the visit for
   * the form, the owner for the list of every visit and their names.
   *
   * Technical
   * ---------
   * `readerTask` holds the parent, `readerSubTask` the child, and each is
   * fetched by its own worker so that a slice has ONE writer on this path. An
   * earlier shape sent both through `fetchReaderTaskWorker`; both wrote
   * `readerTask` and the later response won, leaving a child where the parent
   * belonged (C3). `lazyPrefetchSubtasksWorker` can still overwrite the slice
   * later — which is why this checks the id rather than mere presence.
   */
  const isParentAlreadyLoaded = readerTaskState?.data?.id === taskId;

  /**
   * The reading's studies are answered once per reading, and never reused
   *
   * Plain terms
   * -----------
   * The list of every visit's images belongs to one reading. Opening another
   * reading must not show the previous one's images, and moving between the
   * visits of the same reading must not ask for the same list again.
   *
   * Technical
   * ---------
   * `readingDicomStudiesTaskId` records the parent the list was fetched for.
   * A mismatch clears it here — before `all(...)` runs, so no consumer can read
   * the previous reading's studies in the gap — because an ordinary reading
   * never fetches `dicom/series` and `resetReaderTask` only fires on unmount,
   * which would otherwise let a series-less reading open on leftovers instead
   * of saying "No data to display" (C17). A match skips the request: the
   * reading's studies do not change while the reader walks its visits.
   */
  const isReadingStudiesAlreadyLoaded =
    readerTaskState?.readingDicomStudiesTaskId === taskId;

  if (!isReadingStudiesAlreadyLoaded) {
    yield dispatch(resetReadingDicomStudies(undefined));
  }

  const neededDataRequests: CallEffect[] = [
    call(
      fetchClinicalTrialContextWorker,
      fetchClinicalTrialContext(clinicalTrialId)
    ),
    call(
      fetchClinicalTrialInventoryWorker,
      fetchClinicalTrialInventory(clinicalTrialId)
    ),
    call(
      fetchReaderDICOMResourceWorker,
      fetchReaderDICOMResource({
        clinicalTrialId,
        endpoint: dicomResourceEndpoint,
      })
    ),
  ];

  /**
   * The visit being scored is always asked for fresh, never from the cache
   *
   * Plain terms
   * -----------
   * The reading cache exists to make moving between visits feel instant. The
   * visit the reader is about to score is the one thing that must be current:
   * whether it has been started, what was already entered in it, whether it is
   * finished. That is read from the server, not from memory.
   *
   * Technical
   * ---------
   * `fetchReaderTaskWorker` carries the `readerCache` gate; this leg goes
   * through `fetchReaderSubTaskWorker`, which deliberately does not. It is the
   * same worker `handleGroupTaskSaga` has always used for the child — it feeds
   * `decideToStartSubTask`, which branches on a live `status`, and a cached
   * pre-start copy there would start the wrong visit or skip a start. This leg
   * only runs at all when the sub-task is absent from the slice, so on the
   * grouped path the request has normally already been made and the cache read
   * it used to carry was unreachable anyway. The parent leg below keeps the
   * gate, so an ordinary (non-grouped) reading caches exactly as before.
   */
  if (subTaskId != null && !isSubTaskAlreadyLoaded) {
    neededDataRequests.push(
      call(
        fetchReaderSubTaskWorker,
        fetchReaderSubTask({
          clinicalTrialId,
          taskId,
          subTaskId,
          // parent+sub (two-segment) fetch when a subTaskId is present.
          endpoint: mapUserRoleToTaskEndpoint(selectedRole, true),
        })
      )
    );
  }

  if (!isParentAlreadyLoaded) {
    neededDataRequests.push(
      call(
        fetchReaderTaskWorker,
        fetchReaderTask({
          clinicalTrialId,
          taskId,
          endpoint: mapUserRoleToTaskEndpoint(selectedRole, false),
        })
      )
    );
  }

  /**
   * A grouped reading also asks what images the whole reading has
   *
   * Plain terms
   * -----------
   * Without this the viewer only ever hears about the visit being scored, and
   * the reader cannot compare it against the baseline without leaving the form.
   *
   * Technical
   * ---------
   * Addressed with the PARENT id in a single segment (A1) and gated on the role
   * having such an endpoint at all — `mapUserRoleToDicomSeriesEndpoint` returns
   * null for the roles that never open a reading, and no request is made for
   * them. Independent of `childTasks`, so it runs in parallel with the task
   * legs inside the same `all(...)` (D7-7712).
   */
  const dicomSeriesEndpoint = mapUserRoleToDicomSeriesEndpoint(selectedRole);

  if (subTaskId != null && dicomSeriesEndpoint && !isReadingStudiesAlreadyLoaded) {
    neededDataRequests.push(
      call(
        fetchReadingDicomStudiesWorker,
        fetchReadingDicomStudies({
          clinicalTrialId,
          taskId,
          endpoint: dicomSeriesEndpoint,
        })
      )
    );
  }

  try {
    yield all(neededDataRequests);
    yield dispatch(fetchNeededDataSuccess(undefined));
  } catch (error) {
    console.error(error);
    yield dispatch(fetchNeededDataFailure(error as Error));
  }
}
